import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import Select, { components } from "react-select";
import { canEdit, isSuperAdmin as isSuperAdminUser } from "../utils/permissions";

const emptyRow = (dates = {}) => ({
  share_name: "",
  share_pass: "",
  company_name: "",
  user_name: "",
  user_email: "",
  user_mobile: "",
  duration_years: dates.duration_years || "",
  installed_date: dates.installed_date || "",
  ending_on: dates.ending_on || "",
  to_renew: dates.to_renew || "",
});

const YEAR_OPTIONS = [1, 2, 3, 4, 5];
const MAX_SUB_KEYS = 5;

const ROW_GROUP_COLORS = [
  "#BFDBFE",
  "#BBF7D0",
  "#FED7AA",
  "#DDD6FE",
  "#FBCFE8",
  "#A5F3FC",
];

const buildGroupColorMap = (groupKeys) => {
  const map = {};
  let i = 0;
  groupKeys.forEach((k) => {
    const key = String(k ?? "");
    if (!(key in map)) {
      map[key] = ROW_GROUP_COLORS[i % ROW_GROUP_COLORS.length];
      i += 1;
    }
  });
  return map;
};

const calcEndDate = (start, years) => {
  if (!start || !years) return "";
  return moment(start).add(Number(years), "years").format("YYYY-MM-DD");
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");
const displayDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");

/** Remaining days until Ending On (negative = expired). */
const daysDiff = (endDate) => {
  if (!endDate) return null;
  return moment(endDate).startOf("day").diff(moment().startOf("day"), "days");
};

/** Days until master license end (from MS Key activated / end dates). */
const masterDiffDays = (msKey) => {
  if (!msKey) return null;
  if (msKey.activated_end_date) return daysDiff(msKey.activated_end_date);
  if (msKey.activated_date && msKey.duration_years) {
    return moment(msKey.activated_date)
      .startOf("day")
      .add(Number(msKey.duration_years), "years")
      .diff(moment().startOf("day"), "days");
  }
  if (msKey.activated_date) {
    return moment(msKey.activated_date)
      .startOf("day")
      .add(365, "days")
      .diff(moment().startOf("day"), "days");
  }
  return null;
};

const selectStylesSm = {
  control: (base, state) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
    fontSize: "13px",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "31px" }),
  indicatorsContainer: (base) => ({ ...base, height: "31px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    minWidth: "100%",
    width: "max-content",
    maxWidth: "720px",
  }),
  menuList: (base) => ({ ...base, maxHeight: "320px" }),
  option: (base) => ({ ...base, fontSize: "13px", whiteSpace: "normal" }),
  placeholder: (base) => ({ ...base, fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", maxWidth: "100%" }),
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    fontSize: "14px",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10050,
    minWidth: "100%",
    width: "max-content",
    maxWidth: "720px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  option: (base) => ({ ...base, fontSize: "13px", padding: "8px 12px", whiteSpace: "normal" }),
  singleValue: (base) => ({ ...base, maxWidth: "100%" }),
};

const QuotaBadges = ({ used, left, max, dark }) => (
  <span className={`quota-opt-badges ${dark ? "dark" : ""}`}>
    <span className="opt-badge used">
      Used {used}/{max}
    </span>
    <span className={`opt-badge ${left === 0 ? "full" : "left"}`}>Left {left}</span>
  </span>
);

const QuotaOption = (props) => (
  <components.Option {...props}>
    <div className="quota-opt-row" title={props.data.label || ""}>
      <span className="quota-opt-label">{props.data.label}</span>
      {props.data.used != null && (
        <QuotaBadges used={props.data.used} left={props.data.left} max={props.data.max} />
      )}
    </div>
  </components.Option>
);

const QuotaSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="quota-opt-row compact" title={props.data.label || ""}>
      <span className="quota-opt-label">{props.data.label}</span>
      {props.data.used != null && (
        <QuotaBadges
          used={props.data.used}
          left={props.data.left}
          max={props.data.max}
          dark
        />
      )}
    </div>
  </components.SingleValue>
);

export default function MsSubKeyList() {
  const { http, user } = AuthUser();
  const [masters, setMasters] = useState([]);
  const [subKeys, setSubKeys] = useState([]);
  const [allSubKeys, setAllSubKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterMaster, setFilterMaster] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDays, setFilterDays] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [selectedMasterId, setSelectedMasterId] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [visiblePass, setVisiblePass] = useState({});

  const isSuperAdmin = isSuperAdminUser(user);
  const allowEdit = canEdit(user);

  useEffect(() => {
    fetchMasters();
    fetchSubKeys();
  }, []);

  const fetchMasters = () => {
    http.get("/ms_keys").then((res) => {
      if (res.data.success) setMasters(res.data.data || []);
    });
  };

  const fetchSubKeys = (msKeyId = "") => {
    setIsLoading(true);
    http
      .get("/ms_sub_keys")
      .then((res) => {
        setIsLoading(false);
        if (res.data.success) {
          const all = res.data.data || [];
          setAllSubKeys(all);
          setSubKeys(
            msKeyId ? all.filter((s) => String(s.ms_key_id) === String(msKeyId)) : all
          );
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Error fetching sub keys");
      });
  };

  const datesFromMaster = (masterId) => {
    const master = masters.find((m) => String(m.id) === String(masterId));
    if (!master) return {};
    const installed_date = formatDate(master.activated_date);
    const duration_years = master.duration_years ? String(master.duration_years) : "";
    const ending_on =
      calcEndDate(installed_date, duration_years) || formatDate(master.activated_end_date);
    return {
      duration_years,
      installed_date,
      ending_on,
      to_renew: ending_on,
    };
  };

  const openAddModal = () => {
    setEditingRow(null);
    setSelectedMasterId("");
    setRows([emptyRow()]);
    setShowModal(true);
  };

  const handleMasterChange = (msKeyId) => {
    setSelectedMasterId(msKeyId);
    const dates = datesFromMaster(msKeyId);
    const used = allSubKeys.filter((s) => String(s.ms_key_id) === String(msKeyId)).length;
    const left = Math.max(0, MAX_SUB_KEYS - used);
    if (left <= 0) {
      setRows([emptyRow(dates)]);
      toast.error(`This master already has ${MAX_SUB_KEYS} sub keys`);
      return;
    }
    setRows([emptyRow(dates)]);
  };

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value };
        if (field === "installed_date" || field === "duration_years") {
          const end = calcEndDate(
            field === "installed_date" ? value : next.installed_date,
            field === "duration_years" ? value : next.duration_years
          );
          if (end) {
            next.ending_on = end;
            next.to_renew = end;
          }
        }
        if (field === "ending_on") next.to_renew = value;
        return next;
      })
    );
  };

  const addRow = () => {
    if (!selectedMasterId) {
      toast.error("Select a Master Key first");
      return;
    }
    const used = usedSubCount;
    const left = Math.max(0, MAX_SUB_KEYS - used);
    if (rows.length >= left) {
      toast.error(`Only ${left} sub key slot(s) left (max ${MAX_SUB_KEYS})`);
      return;
    }
    setRows((prev) => [...prev, emptyRow(datesFromMaster(selectedMasterId))]);
    setTimeout(() => {
      const body = document.getElementById("subkey-modal-body");
      if (body) body.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
    }, 50);
  };

  const removeRow = (index) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleEdit = (row) => {
    setEditingRow(row);
    setSelectedMasterId(String(row.ms_key_id));
    setRows([
      {
        share_name: row.share_name || "",
        share_pass: row.share_pass || "",
        company_name: row.company_name || "",
        user_name: row.user_name || "",
        user_email: row.user_email || "",
        user_mobile: row.user_mobile || "",
        duration_years: row.duration_years ? String(row.duration_years) : "",
        installed_date: formatDate(row.installed_date),
        ending_on: formatDate(row.ending_on),
        to_renew: formatDate(row.to_renew),
      },
    ]);
    setShowModal(true);
  };

  const resetModal = () => {
    setShowModal(false);
    setEditingRow(null);
    setSelectedMasterId("");
    setRows([emptyRow()]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowEdit) {
      toast.error("View-only users cannot modify data");
      return;
    }
    if (!selectedMasterId) {
      toast.error("Please select a Master Key");
      return;
    }

    const filled = rows.filter(
      (r) =>
        r.share_name ||
        r.share_pass ||
        r.company_name ||
        r.user_name ||
        r.user_email ||
        r.user_mobile
    );
    if (filled.length === 0) {
      toast.error("Enter at least one sub key detail");
      return;
    }

    if (!editingRow) {
      const used = allSubKeys.filter((s) => String(s.ms_key_id) === String(selectedMasterId)).length;
      const left = Math.max(0, MAX_SUB_KEYS - used);
      if (filled.length > left) {
        toast.error(`Only ${left} sub key(s) left for this master (max ${MAX_SUB_KEYS})`);
        return;
      }
    }

    setIsLoading(true);
    try {
      if (editingRow) {
        const row = filled[0] || rows[0];
        await http.put(`/ms_sub_keys/${editingRow.id}`, {
          ms_key_id: selectedMasterId,
          ...row,
        });
        toast.success("Sub key updated successfully");
      } else {
        await http.post("/ms_sub_keys", {
          ms_key_id: selectedMasterId,
          sub_keys: filled,
        });
        toast.success(`${filled.length} sub key(s) created`);
      }
      resetModal();
      fetchSubKeys(filterMaster);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving sub keys");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sub key?")) return;
    try {
      await http.delete(`/ms_sub_keys/${id}`);
      toast.success("Sub key deleted");
      fetchSubKeys(filterMaster);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting sub key");
    }
  };

  const filtered = subKeys
    .filter((row) => {
      const q = searchTerm.toLowerCase();
      if (q) {
        const hit = [
          row.share_name,
          row.company_name,
          row.user_name,
          row.user_email,
          row.user_mobile,
          row.ms_key?.master_key_id,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
        if (!hit) return false;
      }
      if (filterDays !== "") {
        const d = daysDiff(row.ending_on);
        if (d === null) return false;
        if (filterDays === "expired") return d < 0;
        return d < Number(filterDays);
      }
      return true;
    })
    .slice()
    .sort((a, b) => {
      const ma = String(a.ms_key?.master_key_id || a.ms_key_id || "");
      const mb = String(b.ms_key?.master_key_id || b.ms_key_id || "");
      return ma.localeCompare(mb) || Number(a.id) - Number(b.id);
    });

  const renderDaysDiff = (endDate) => {
    const d = daysDiff(endDate);
    if (d === null) return <span className="text-muted">—</span>;
    if (d < 0) {
      return (
        <span className="badge bg-danger" title="Expired">
          {d}
        </span>
      );
    }
    if (d < 30) {
      return (
        <span className="badge bg-warning text-dark" title="Expiring soon">
          {d}
        </span>
      );
    }
    return <span className="fw-bold text-success">{d}</span>;
  };

  const renderMasterDiff = (msKey) => {
    const d = masterDiffDays(msKey);
    if (d === null) return <span className="text-muted">—</span>;
    const title =
      msKey?.activated_date || msKey?.activated_end_date
        ? [
            msKey.activated_date
              ? `Activated ${moment(msKey.activated_date).format("DD/MM/YYYY")}`
              : null,
            msKey.activated_end_date
              ? `End ${moment(msKey.activated_end_date).format("DD/MM/YYYY")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : "";
    if (d < 0) {
      return (
        <span className="badge bg-danger" title={title}>
          {d}
        </span>
      );
    }
    if (d < 30) {
      return (
        <span className="badge bg-warning text-dark" title={title}>
          {d}
        </span>
      );
    }
    return (
      <span className="fw-bold text-success" title={title}>
        {d}
      </span>
    );
  };

  const groupColors = buildGroupColorMap(filtered.map((r) => r.ms_key_id));

  const usedSubCount = selectedMasterId
    ? allSubKeys.filter((s) => String(s.ms_key_id) === String(selectedMasterId)).length
    : 0;
  const leftSubCount = Math.max(0, MAX_SUB_KEYS - usedSubCount);
  const canAddMoreRows = !editingRow && selectedMasterId && rows.length < leftSubCount;

  const masterOptions = [
    { value: "", label: "All Master Keys" },
    ...masters.map((m) => {
      const used = allSubKeys.filter((s) => String(s.ms_key_id) === String(m.id)).length;
      const left = Math.max(0, MAX_SUB_KEYS - used);
      return {
        value: String(m.id),
        label: m.master_key_id || `Master #${m.id}`,
        used,
        left,
        max: MAX_SUB_KEYS,
        searchText: `${m.master_key_id} used ${used} left ${left}`,
      };
    }),
  ];

  const modalMasterOptions = masters.map((m) => {
    const used = allSubKeys.filter((s) => String(s.ms_key_id) === String(m.id)).length;
    const left = Math.max(0, MAX_SUB_KEYS - used);
    return {
      value: String(m.id),
      label: m.master_key_id || `Master #${m.id}`,
      used,
      left,
      max: MAX_SUB_KEYS,
      isDisabled: left === 0,
      searchText: `${m.master_key_id} used ${used} left ${left}`,
    };
  });

  const quotaFilterOption = (option, input) => {
    const q = String(input || "").toLowerCase();
    if (!q) return true;
    return (
      String(option.data.label || "").toLowerCase().includes(q) ||
      String(option.data.searchText || "").toLowerCase().includes(q)
    );
  };

  return (
    <div className="container-fluid mt-3 subkey-page">
      <Toaster />
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
          <div>
            <h4 className="mb-0">Sub Keys</h4>
            <small className="text-muted">Linked to a Master Key</small>
          </div>
          {allowEdit && (
          <button className="btn btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus-circle me-1"></i> Add Sub Keys
          </button>
          )}
        </div>
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
            <div
              className="sub-master-filter"
              style={{ minWidth: "420px", maxWidth: "560px", flex: "1 1 420px" }}
              title={
                (masterOptions.find((o) => o.value === String(filterMaster)) ||
                  masterOptions[0])?.label || ""
              }
            >
              <Select
                options={masterOptions}
                value={
                  masterOptions.find((o) => o.value === String(filterMaster)) ||
                  masterOptions[0]
                }
                onChange={(opt) => {
                  const val = opt?.value || "";
                  setFilterMaster(val);
                  fetchSubKeys(val);
                }}
                placeholder="Search master key..."
                isSearchable
                filterOption={quotaFilterOption}
                components={{ Option: QuotaOption, SingleValue: QuotaSingleValue }}
                styles={selectStylesSm}
              />
            </div>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: "260px" }}
              placeholder="Search share / company / user / email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div style={{ minWidth: "180px" }}>
              <label
                className="form-label mb-1 fw-semibold"
                style={{ fontSize: "12px", color: "#0d6efd" }}
              >
                Days Diff
              </label>
              <select
                className="form-select form-select-sm"
                value={filterDays}
                onChange={(e) => setFilterDays(e.target.value)}
              >
                <option value="">All Days</option>
                <option value="30">Less than 30 Days</option>
                <option value="60">Less than 60 Days</option>
                <option value="90">Less than 90 Days</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setSearchTerm("");
                setFilterDays("");
                setFilterMaster("");
                fetchSubKeys("");
              }}
            >
              Clear
            </button>
          </div>

          {isLoading && subKeys.length === 0 ? (
            <TaskSkeleton />
          ) : (
            <div className="table-responsive subkey-table-wrap">
              <table className="table table-hover align-middle mb-0 subkey-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Master Key</th>
                    <th>Master Activated</th>
                    <th>Master Diff</th>
                    <th>Share Name</th>
                    <th>Share Pass</th>
                    <th>Company Name</th>
                    <th>User Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Duration</th>
                    <th>Installed</th>
                    <th>Ending On</th>
                    <th>Days Diff</th>
                    <th>To Renew</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((row, index) => (
                      <tr
                        key={row.id}
                        className="group-row"
                        style={{
                          "--row-bg": groupColors[String(row.ms_key_id)] || "#BFDBFE",
                        }}
                      >
                        <td>{index + 1}</td>
                        <td className="fw-semibold">
                          <div
                            className="trunc-text"
                            style={{ maxWidth: "140px" }}
                            title={row.ms_key?.master_key_id || ""}
                          >
                            {row.ms_key?.master_key_id || "—"}
                          </div>
                        </td>
                        <td className="text-nowrap">
                          {displayDate(row.ms_key?.activated_date)}
                        </td>
                        <td className="text-center">
                          {renderMasterDiff(row.ms_key)}
                        </td>
                        <td>
                          <div
                            className="trunc-text"
                            style={{ maxWidth: "120px" }}
                            title={row.share_name || ""}
                          >
                            {row.share_name || "—"}
                          </div>
                        </td>
                        <td>
                          {row.share_pass ? (
                            <span className="d-inline-flex align-items-center gap-1">
                              <span className="font-monospace" style={{ fontSize: "12px" }}>
                                {visiblePass[row.id] ? row.share_pass : "••••••"}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-light border"
                                style={{ padding: "1px 6px" }}
                                onClick={() =>
                                  setVisiblePass((p) => ({ ...p, [row.id]: !p[row.id] }))
                                }
                              >
                                <i className={`fas ${visiblePass[row.id] ? "fa-eye-slash" : "fa-eye"}`}></i>
                              </button>
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div
                            className="trunc-text"
                            style={{ maxWidth: "110px" }}
                            title={row.company_name || ""}
                          >
                            {row.company_name || "—"}
                          </div>
                        </td>
                        <td>
                          <div
                            className="trunc-text"
                            style={{ maxWidth: "90px" }}
                            title={row.user_name || ""}
                          >
                            {row.user_name || "—"}
                          </div>
                        </td>
                        <td>
                          <div
                            className="trunc-text"
                            style={{ maxWidth: "120px" }}
                            title={row.user_email || ""}
                          >
                            {row.user_email || "—"}
                          </div>
                        </td>
                        <td>{row.user_mobile || "—"}</td>
                        <td>
                          {row.duration_years ? (
                            <span className="badge bg-primary-subtle text-primary border">
                              {row.duration_years} Yr{row.duration_years > 1 ? "s" : ""}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="text-nowrap">{displayDate(row.installed_date)}</td>
                        <td className="text-nowrap">{displayDate(row.ending_on)}</td>
                        <td className="text-center">{renderDaysDiff(row.ending_on)}</td>
                        <td className="text-nowrap">{displayDate(row.to_renew)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {allowEdit && (
                          <button
                            className="btn btn-sm btn-outline-warning me-1"
                            onClick={() => handleEdit(row)}
                            title="Edit"
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          )}
                          {allowEdit && isSuperAdmin && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(row.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                          {!allowEdit && <span className="text-muted">View only</span>}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="16" className="text-center text-muted py-4">
                        No sub keys found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block subkey-modal" style={{ backgroundColor: "rgba(15,23,42,0.45)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content border-0 shadow">
              <form onSubmit={handleSubmit} className="subkey-form">
                <div className="modal-header border-0 pb-0">
                  <div>
                    <h5 className="modal-title mb-1">{editingRow ? "Edit Sub Key" : "Add Sub Keys"}</h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Choose a master, then fill share, user and billing details
                    </p>
                  </div>
                  <button type="button" className="btn-close" onClick={resetModal}></button>
                </div>
                <div className="modal-body pt-3" id="subkey-modal-body">
                  <div className="master-pick mb-4">
                    <label className="form-label fw-semibold mb-1">
                      Master Key <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={modalMasterOptions}
                      value={
                        modalMasterOptions.find(
                          (o) => o.value === String(selectedMasterId)
                        ) || null
                      }
                      onChange={(opt) => handleMasterChange(opt?.value || "")}
                      placeholder="Search / select master key..."
                      isSearchable
                      isDisabled={!!editingRow}
                      filterOption={quotaFilterOption}
                      components={{ Option: QuotaOption, SingleValue: QuotaSingleValue }}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    <div className="hint">
                      Duration and dates copy from the master. Change duration to recalculate ending / renew.
                    </div>
                    {selectedMasterId && !editingRow && (
                      <div className="quota-bar mt-2">
                        <span className="quota-pill used">
                          Used: <b>{usedSubCount}</b> / {MAX_SUB_KEYS}
                        </span>
                        <span className={`quota-pill ${leftSubCount === 0 ? "full" : "left"}`}>
                          Left: <b>{leftSubCount}</b>
                        </span>
                        <span className="quota-note">
                          Adding now: {rows.length} · After save: {usedSubCount + rows.length}/{MAX_SUB_KEYS}
                        </span>
                      </div>
                    )}
                  </div>

                  {rows.map((row, index) => (
                    <div key={index} className="subkey-card">
                      <div className="subkey-card-head">
                        <span className="subkey-num">{index + 1}</span>
                        <span className="fw-semibold">Sub Key {index + 1}</span>
                        {!editingRow && rows.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-auto"
                            onClick={() => removeRow(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="subkey-section share">
                        <div className="section-title">Share</div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Share Name</label>
                            <input
                              className="form-control"
                              placeholder="Share name"
                              value={row.share_name}
                              onChange={(e) => updateRow(index, "share_name", e.target.value)}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Share Pass</label>
                            <input
                              className="form-control"
                              placeholder="Share password"
                              autoComplete="off"
                              value={row.share_pass}
                              onChange={(e) => updateRow(index, "share_pass", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="subkey-section user">
                        <div className="section-title">User</div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Company Name</label>
                            <input
                              className="form-control"
                              placeholder="Company name"
                              value={row.company_name}
                              onChange={(e) => updateRow(index, "company_name", e.target.value)}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input
                              className="form-control"
                              placeholder="User name"
                              value={row.user_name}
                              onChange={(e) => updateRow(index, "user_name", e.target.value)}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Email</label>
                            <input
                              type="email"
                              className="form-control"
                              placeholder="email@example.com"
                              value={row.user_email}
                              onChange={(e) => updateRow(index, "user_email", e.target.value)}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Mobile</label>
                            <input
                              className="form-control"
                              placeholder="Mobile number"
                              value={row.user_mobile}
                              onChange={(e) => updateRow(index, "user_mobile", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="subkey-section billing">
                        <div className="section-title">Billing period</div>
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Installed Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={row.installed_date}
                              onChange={(e) => updateRow(index, "installed_date", e.target.value)}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Duration</label>
                            <select
                              className="form-select"
                              value={row.duration_years}
                              onChange={(e) => updateRow(index, "duration_years", e.target.value)}
                            >
                              <option value="">Select years</option>
                              {YEAR_OPTIONS.map((y) => (
                                <option key={y} value={y}>
                                  {y} Year{y > 1 ? "s" : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Ending On</label>
                            <input
                              type="date"
                              className="form-control bg-light"
                              value={row.ending_on}
                              onChange={(e) => updateRow(index, "ending_on", e.target.value)}
                              readOnly={!!row.duration_years}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">To Renew</label>
                            <input
                              type="date"
                              className="form-control"
                              value={row.to_renew}
                              onChange={(e) => updateRow(index, "to_renew", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {!editingRow && (
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100 py-2"
                      onClick={addRow}
                      disabled={!canAddMoreRows}
                    >
                      {leftSubCount === 0 && selectedMasterId
                        ? `Limit reached (${MAX_SUB_KEYS}/${MAX_SUB_KEYS})`
                        : `+ Add another sub key${selectedMasterId ? ` (${leftSubCount - rows.length} more can add)` : ""}`}
                    </button>
                  )}
                  {selectedMasterId && !editingRow && leftSubCount === 0 && (
                    <div className="text-danger text-center mt-2" style={{ fontSize: "13px" }}>
                      This master already has {MAX_SUB_KEYS} sub keys. No slots left.
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-light border" onClick={resetModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading || (!editingRow && selectedMasterId && leftSubCount === 0)}
                  >
                    {isLoading ? "Saving..." : editingRow ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .subkey-modal {
          overflow: hidden;
        }
        .subkey-modal .modal-dialog {
          max-width: 1100px;
          margin: 16px auto;
          height: calc(100vh - 32px);
          max-height: calc(100vh - 32px);
        }
        .subkey-modal .modal-content {
          height: 100%;
          max-height: 100%;
          display: flex;
          flex-direction: column;
        }
        .subkey-form {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
        .subkey-modal .modal-header,
        .subkey-modal .modal-footer {
          flex: 0 0 auto;
        }
        .subkey-modal .modal-body {
          flex: 1 1 auto;
          overflow-y: auto;
          min-height: 0;
          -webkit-overflow-scrolling: touch;
        }
        .subkey-page .form-label {
          font-size: 12px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 4px;
        }
        .subkey-table-wrap {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: auto;
          max-height: 70vh;
        }
        .subkey-table thead th {
          position: sticky;
          top: 0;
          background: #0f766e;
          color: #fff;
          font-size: 12px;
          white-space: nowrap;
          z-index: 1;
        }
        .subkey-table td {
          font-size: 13px;
        }
        .subkey-table .trunc-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
          cursor: default;
        }
        .subkey-table tbody tr.group-row > td {
          background-color: var(--row-bg) !important;
        }
        .subkey-table tbody tr.group-row:hover > td {
          filter: brightness(0.97);
        }
        .master-pick {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px 16px;
        }
        .master-pick .hint {
          font-size: 12px;
          color: #64748b;
          margin-top: 6px;
        }
        .subkey-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 16px;
          background: #fff;
        }
        .subkey-card-head {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #0f172a;
          color: #fff;
        }
        .subkey-num {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: #14b8a6;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
        }
        .subkey-section {
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
        }
        .subkey-section.share { background: #fffbeb; }
        .subkey-section.user { background: #ecfeff; }
        .subkey-section.billing { background: #fff; }
        .quota-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .quota-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border-radius: 999px;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .quota-pill.used {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .quota-pill.left {
          background: #dcfce7;
          color: #166534;
        }
        .quota-pill.full {
          background: #fee2e2;
          color: #b91c1c;
        }
        .quota-opt-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          width: 100%;
          cursor: help;
        }
        .quota-opt-row.compact {
          gap: 8px;
        }
        .quota-opt-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          cursor: help;
        }
        .quota-opt-badges {
          display: inline-flex;
          flex-shrink: 0;
          gap: 4px;
        }
        .opt-badge {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 1px 8px;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.4;
          white-space: nowrap;
        }
        .opt-badge.used {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .opt-badge.left {
          background: #dcfce7;
          color: #166534;
        }
        .opt-badge.full {
          background: #fee2e2;
          color: #b91c1c;
        }
        .quota-opt-badges.dark .opt-badge.used {
          background: #bfdbfe;
        }
        .quota-opt-badges.dark .opt-badge.left {
          background: #bbf7d0;
        }
        .quota-opt-badges.dark .opt-badge.full {
          background: #fecaca;
        }
        .quota-note {
          font-size: 12px;
          color: #64748b;
        }
        .section-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
}
